(function (global) {
  'use strict';

  const DIMENSIONS = ['Understand', 'Evaluate', 'Adopt', 'Lead'];

  const QUESTIONS = [
    {
      id: 'U1',
      dimension: 'Understand',
      text: "I can explain what today's generative AI is good at—and where it is unreliable—in practical business terms."
    },
    {
      id: 'U2',
      dimension: 'Understand',
      text: 'I can identify the kinds of business tasks where AI is likely to create useful leverage.'
    },
    {
      id: 'U3',
      dimension: 'Understand',
      text: 'I understand how instructions, context, source material, and review affect the quality of AI output.'
    },
    {
      id: 'E1',
      dimension: 'Evaluate',
      text: 'In the work I influence, AI opportunities are judged using business value, feasibility, ownership, and measurable outcomes—not novelty alone.'
    },
    {
      id: 'E2',
      dimension: 'Evaluate',
      text: 'I can compare AI platforms based on their strengths, integrations, cost, and fit for the work.'
    },
    {
      id: 'E3',
      dimension: 'Evaluate',
      text: 'In my role or area, we can test an AI-assisted workflow against the current process and judge whether it is actually better.'
    },
    {
      id: 'A1',
      dimension: 'Adopt',
      text: 'In the work I oversee or contribute to, we have identified specific workflows or decisions where AI may help—not just a vague goal to “use more AI.”'
    },
    {
      id: 'A2',
      dimension: 'Adopt',
      text: 'The people I work with can access appropriate AI tools, useful examples, and reusable instructions for their work.'
    },
    {
      id: 'A3',
      dimension: 'Adopt',
      text: 'In my area, a useful AI experiment can become a repeatable workflow with an owner, quality standard, and review point.'
    },
    {
      id: 'L1',
      dimension: 'Lead',
      text: 'The leaders I work with communicate a clear reason for using AI and realistic expectations about what it can improve.'
    },
    {
      id: 'L2',
      dimension: 'Lead',
      text: 'I have seen leaders in my area demonstrate at least one useful AI workflow rather than discussing AI only in abstract terms.'
    },
    {
      id: 'L3',
      dimension: 'Lead',
      text: 'In my role or area, I can identify the AI decisions and actions that matter most over the next 90 days.'
    }
  ];

  const ANSWERS = [
    { value: 0, label: 'Not yet true', detail: 'I have not seen or established this yet.' },
    { value: 1, label: 'Informal or inconsistent', detail: 'It happens, but it depends on the person or situation.' },
    { value: 2, label: 'Mostly true', detail: 'A working approach exists in the area I know, with some gaps.' },
    { value: 3, label: 'Clear and repeatable', detail: 'This is clear and consistently applied in the work I can observe.' }
  ];

  const LEVELS = [
    {
      min: 0,
      max: 9,
      key: 'reactive',
      name: 'Reactive',
      headline: 'Your next advantage comes from building a practical AI foundation.',
      body: 'Your decisions may currently be shaped by scattered experiments, individual enthusiasm, or vendor explanations. The best next move is to experience useful AI work directly and establish a shared language for judging it.'
    },
    {
      min: 10,
      max: 18,
      key: 'exploring',
      name: 'Exploring',
      headline: 'You have momentum. Now turn experimentation into a repeatable way of working.',
      body: 'You likely have useful awareness and some hands-on experience, but the quality of decisions may still vary by person or team. A shared method for choosing tools, testing workflows, and measuring improvement will create leverage.'
    },
    {
      min: 19,
      max: 27,
      key: 'preparing',
      name: 'Preparing',
      headline: 'You have a workable foundation. The opportunity is consistent execution.',
      body: 'Your organization appears ready to move beyond isolated experiments. Focus on turning the best use cases into owned workflows, reusable operating practices, and a prioritized 90-day adoption path.'
    },
    {
      min: 28,
      max: 36,
      key: 'leading',
      name: 'Leading Deliberately',
      headline: 'Your AI decisions are becoming structured, practical, and intentional.',
      body: 'You have a strong base across understanding, evaluation, adoption, and leadership. Keep expanding hands-on fluency, share what works across the organization, and update your approach as platforms and capabilities change.'
    }
  ];

  const GAP_COPY = {
    Understand: {
      summary: 'Your highest-leverage opportunity is building a clearer working model of what current AI platforms can actually do.',
      action: 'Choose one recurring task and complete it with ChatGPT, Claude, or Gemini. Record the context you provided, the output you received, and the revision that made it useful.'
    },
    Evaluate: {
      summary: 'Your highest-leverage opportunity is learning to compare tools and workflows using consistent business criteria.',
      action: 'Run the same real task through two AI platforms. Compare output quality, speed, integrations, cost, and how much refinement each result required.'
    },
    Adopt: {
      summary: 'Your highest-leverage opportunity is turning useful experiments into repeatable ways of working.',
      action: 'Take your strongest AI use case and document the trigger, input, tool, instruction, output standard, owner, and final handoff.'
    },
    Lead: {
      summary: 'Your highest-leverage opportunity is creating visible leadership direction around where AI should produce advantage.',
      action: 'Choose the three AI-enabled improvements that matter most in the next 90 days, assign an owner to each, and demonstrate one workflow to your leadership team.'
    }
  };

  function getLevel(total) {
    return LEVELS.find(function (level) {
      return total >= level.min && total <= level.max;
    });
  }

  function calculateScore(answerInput) {
    const normalized = {};
    QUESTIONS.forEach(function (question, index) {
      const raw = Array.isArray(answerInput) ? answerInput[index] : answerInput[question.id];
      const value = Number(raw);
      normalized[question.id] = Number.isInteger(value) && value >= 0 && value <= 3 ? value : 0;
    });

    const scores = { Understand: 0, Evaluate: 0, Adopt: 0, Lead: 0 };
    QUESTIONS.forEach(function (question) {
      scores[question.dimension] += normalized[question.id];
    });

    const total = DIMENSIONS.reduce(function (sum, dimension) {
      return sum + scores[dimension];
    }, 0);

    let priorityGap = DIMENSIONS[0];
    let strongestDimension = DIMENSIONS[0];
    DIMENSIONS.forEach(function (dimension) {
      if (scores[dimension] < scores[priorityGap]) priorityGap = dimension;
      if (scores[dimension] > scores[strongestDimension]) strongestDimension = dimension;
    });

    return {
      answers: normalized,
      scores: scores,
      total: total,
      level: getLevel(total),
      priorityGap: priorityGap,
      strongestDimension: strongestDimension
    };
  }

  function slug(value) {
    return String(value || '')
      .trim()
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '_')
      .replace(/^_|_$/g, '');
  }

  function emitEvent(name, parameters) {
    const detail = parameters || {};
    global.dataLayer = global.dataLayer || [];
    global.dataLayer.push(Object.assign({ event: name }, detail));
    if (global.document) {
      global.document.dispatchEvent(new CustomEvent('aif:' + name, { detail: detail }));
    }
  }

  function buildKlaviyoProperties(scoreResult, teamSize, identity) {
    return {
      email: identity.email,
      first_name: identity.firstName,
      last_name: identity.lastName,
      scorecard_completed: true,
      scorecard_total: scoreResult.total,
      scorecard_understand: scoreResult.scores.Understand,
      scorecard_evaluate: scoreResult.scores.Evaluate,
      scorecard_adopt: scoreResult.scores.Adopt,
      scorecard_lead: scoreResult.scores.Lead,
      scorecard_readiness: scoreResult.level.key,
      scorecard_readiness_label: scoreResult.level.name,
      scorecard_strongest: scoreResult.strongestDimension,
      scorecard_priority_gap: scoreResult.priorityGap,
      scorecard_route: teamSize === 'just_me' ? 'individual' : 'team',
      scorecard_team_size: teamSize,
      scorecard_last_completed_at: new Date().toISOString()
    };
  }

  function syncKlaviyo(scoreResult, teamSize, identity, done) {
    const properties = buildKlaviyoProperties(scoreResult, teamSize, identity);
    const eventProperties = Object.assign({}, properties);
    delete eventProperties.email;
    delete eventProperties.first_name;
    delete eventProperties.last_name;

    let finished = false;
    function finish() {
      if (finished) return;
      finished = true;
      done();
    }

    const fallbackTimer = global.setTimeout(finish, 1500);
    function finishSoon() {
      global.setTimeout(function () {
        global.clearTimeout(fallbackTimer);
        finish();
      }, 200);
    }

    try {
      if (global.klaviyo && typeof global.klaviyo.identify === 'function') {
        global.klaviyo.identify(properties, function () {
          global.klaviyo.track('Scorecard Completed', eventProperties);
          finishSoon();
        });
        return;
      }
      if (global._learnq && typeof global._learnq.push === 'function') {
        global._learnq.push(['identify', properties]);
        global._learnq.push(['track', 'Scorecard Completed', eventProperties]);
        finishSoon();
        return;
      }
    } catch (error) {
      // Shopify submission remains the source of truth if Klaviyo is unavailable.
    }
    global.clearTimeout(fallbackTimer);
    finish();
  }

  function initScorecard(root) {
    if (!root || root.dataset.initialized === 'true') return;
    root.dataset.initialized = 'true';

    const stages = {};
    root.querySelectorAll('[data-stage]').forEach(function (stage) {
      stages[stage.dataset.stage] = stage;
    });

    const questionHeading = root.querySelector('[data-question-heading]');
    const questionDimension = root.querySelector('[data-question-dimension]');
    const questionPosition = root.querySelector('[data-question-position]');
    const progressBar = root.querySelector('[data-progress-bar]');
    const answersWrap = root.querySelector('[data-answer-options]');
    const backButton = root.querySelector('[data-back]');
    const continueButton = root.querySelector('[data-continue]');
    const form = root.querySelector('.aif-scorecard__capture-form');
    const formState = form.querySelector('.aif-scorecard__form-state');
    const formError = root.querySelector('[data-form-error]');
    const tagInput = root.querySelector('[data-scorecard-tags]');
    const teamInput = root.querySelector('[data-team-size]');
    const liveRegion = root.querySelector('[data-live-region]');
    const storageKey = 'aif-scorecard-state:' + root.id;
    const answers = {};
    let questionIndex = 0;
    let result = null;

    function saveState(extra) {
      const state = Object.assign(
        {
          answers: answers,
          questionIndex: questionIndex,
          result: result,
          teamSize: teamInput ? teamInput.value : ''
        },
        extra || {}
      );
      try {
        global.sessionStorage.setItem(storageKey, JSON.stringify(state));
      } catch (error) {
        // The scorecard remains usable when browser storage is unavailable.
      }
    }

    function loadState() {
      try {
        const stored = JSON.parse(global.sessionStorage.getItem(storageKey) || 'null');
        if (!stored) return null;
        Object.keys(stored.answers || {}).forEach(function (key) {
          answers[key] = stored.answers[key];
        });
        questionIndex = Number.isInteger(stored.questionIndex) ? stored.questionIndex : 0;
        result = stored.result || null;
        if (teamInput && stored.teamSize) teamInput.value = stored.teamSize;
        return stored;
      } catch (error) {
        return null;
      }
    }

    function announce(message) {
      if (!liveRegion) return;
      liveRegion.textContent = '';
      global.setTimeout(function () {
        liveRegion.textContent = message;
      }, 40);
    }

    function showStage(name, focusTarget) {
      Object.keys(stages).forEach(function (stageName) {
        const active = stageName === name;
        stages[stageName].hidden = !active;
        stages[stageName].setAttribute('aria-hidden', active ? 'false' : 'true');
      });
      root.dataset.currentStage = name;
      global.requestAnimationFrame(function () {
        const focusElement = focusTarget || stages[name].querySelector('h1, h2, [tabindex="-1"]');
        if (focusElement) focusElement.focus({ preventScroll: true });
        root.scrollIntoView({ behavior: 'smooth', block: 'start' });
      });
    }

    function renderQuestion() {
      const question = QUESTIONS[questionIndex];
      questionDimension.textContent = question.dimension;
      questionPosition.textContent = 'Question ' + (questionIndex + 1) + ' of ' + QUESTIONS.length;
      questionHeading.textContent = question.text;
      progressBar.style.width = ((questionIndex + 1) / QUESTIONS.length) * 100 + '%';
      progressBar.parentElement.setAttribute('aria-valuenow', String(questionIndex + 1));
      answersWrap.innerHTML = '';

      ANSWERS.forEach(function (answer) {
        const option = global.document.createElement('label');
        option.className = 'aif-scorecard__answer';
        const radio = global.document.createElement('input');
        radio.type = 'radio';
        radio.name = 'answer-' + question.id;
        radio.value = String(answer.value);
        radio.checked = Number(answers[question.id]) === answer.value;
        radio.addEventListener('change', function () {
          answers[question.id] = answer.value;
          continueButton.disabled = false;
          saveState();
        });
        const copy = global.document.createElement('span');
        copy.className = 'aif-scorecard__answer-copy';
        copy.innerHTML = '<strong>' + answer.value + ' — ' + answer.label + '</strong><small>' + answer.detail + '</small>';
        option.appendChild(radio);
        option.appendChild(copy);
        answersWrap.appendChild(option);
      });

      backButton.textContent = questionIndex === 0 ? 'Back to overview' : 'Back';
      continueButton.textContent = questionIndex === QUESTIONS.length - 1 ? 'See the next step' : 'Continue';
      continueButton.disabled = !Number.isInteger(Number(answers[question.id]));
      saveState();
      showStage('question', questionHeading);
      announce(questionPosition.textContent + '. ' + question.dimension + '.');
      emitEvent('scorecard_question', {
        question_number: questionIndex + 1,
        dimension: question.dimension
      });
    }

    function showCapture() {
      result = calculateScore(answers);
      saveState();
      showStage('capture');
      emitEvent('scorecard_capture_view', {});
    }

    function buildTags(teamSize) {
      return [
        'scorecard_completed',
        'readiness_' + result.level.key,
        'gap_' + slug(result.priorityGap),
        'strongest_' + slug(result.strongestDimension),
        'route_' + (teamSize === 'just_me' ? 'individual' : 'team'),
        'team_' + slug(teamSize),
        'score_total_' + result.total,
        'score_u_' + result.scores.Understand,
        'score_e_' + result.scores.Evaluate,
        'score_a_' + result.scores.Adopt,
        'score_l_' + result.scores.Lead
      ].join(',');
    }

    function renderResult(teamSize) {
      if (!result) result = calculateScore(answers);
      const isTeam = teamSize && teamSize !== 'just_me';
      const dimensionValues = DIMENSIONS.map(function (dimension) { return result.scores[dimension]; });
      const allDimensionsTied = new Set(dimensionValues).size === 1;
      const balancedHigh = allDimensionsTied && result.scores[result.priorityGap] >= 8;
      root.querySelector('[data-result-level]').textContent = result.level.name;
      root.querySelector('[data-result-score]').textContent = result.total + ' / 36';
      root.querySelector('[data-result-headline]').textContent = result.level.headline;
      root.querySelector('[data-result-body]').textContent = result.level.body;
      root.querySelector('[data-result-strongest]').textContent = allDimensionsTied ? 'Balanced across all four' : result.strongestDimension;
      root.querySelector('[data-result-gap]').textContent = balancedHigh ? 'Maintain alignment' : result.priorityGap;
      root.querySelector('[data-result-gap-summary]').textContent = balancedHigh
        ? 'No single dimension stands out as a weakness. Your next opportunity is to keep understanding, evaluation, adoption, and leadership aligned as AI capabilities change.'
        : GAP_COPY[result.priorityGap].summary;
      root.querySelector('[data-result-action]').textContent = balancedHigh
        ? 'Document one proven AI workflow, share it with another leader, and identify the next adjacent task where the same approach could create useful leverage.'
        : GAP_COPY[result.priorityGap].action;

      const dimensionWrap = root.querySelector('[data-dimension-results]');
      dimensionWrap.innerHTML = '';
      DIMENSIONS.forEach(function (dimension) {
        const item = global.document.createElement('div');
        item.className = 'aif-scorecard__dimension';
        item.innerHTML =
          '<div class="aif-scorecard__dimension-heading"><span>' +
          dimension +
          '</span><strong>' +
          result.scores[dimension] +
          ' / 9</strong></div>' +
          '<div class="aif-scorecard__dimension-track"><span style="width:' +
          (result.scores[dimension] / 9) * 100 +
          '%"></span></div>';
        dimensionWrap.appendChild(item);
      });

      const primary = root.querySelector('[data-primary-cta]');
      const secondary = root.querySelector('[data-secondary-cta]');
      if (isTeam) {
        primary.href = root.dataset.teamUrl;
        primary.textContent = 'View Team Access';
        primary.dataset.event = 'scorecard_team_click';
        secondary.href = root.dataset.programUrl;
        secondary.textContent = 'Explore Individual Access';
        secondary.dataset.event = 'scorecard_program_click';
      } else {
        primary.href = root.dataset.programUrl;
        primary.textContent = root.dataset.programLabel || 'Explore the Program — $795 USD';
        primary.dataset.event = 'scorecard_program_click';
        secondary.href = root.dataset.teamUrl;
        secondary.textContent = 'Building fluency across a team? View Team Access';
        secondary.dataset.event = 'scorecard_team_click';
      }

      showStage('result');
      emitEvent('scorecard_result_view', {
        result_level: result.level.name,
        priority_gap: result.priorityGap,
        team_route: isTeam ? 'team' : 'individual'
      });
    }

    root.querySelector('[data-start]').addEventListener('click', function () {
      questionIndex = 0;
      emitEvent('scorecard_start', {});
      renderQuestion();
    });

    backButton.addEventListener('click', function () {
      if (questionIndex === 0) {
        showStage('landing');
        return;
      }
      questionIndex -= 1;
      renderQuestion();
    });

    continueButton.addEventListener('click', function () {
      const question = QUESTIONS[questionIndex];
      if (!Number.isInteger(Number(answers[question.id]))) {
        announce('Choose one response before continuing.');
        return;
      }
      if (questionIndex < QUESTIONS.length - 1) {
        questionIndex += 1;
        renderQuestion();
      } else {
        showCapture();
      }
    });

    form.addEventListener('submit', function (event) {
      if (!form.checkValidity()) {
        event.preventDefault();
        form.reportValidity();
        announce('Complete the required fields before continuing.');
        return;
      }
      result = calculateScore(answers);
      const teamSize = teamInput.value;
      const emailField = form.querySelector('[name="contact[email]"]');
      const firstNameField = form.querySelector('[name="contact[first_name]"]');
      const lastNameField = form.querySelector('[name="contact[last_name]"]');
      const identity = {
        email: emailField ? emailField.value.trim() : '',
        firstName: firstNameField ? firstNameField.value.trim() : '',
        lastName: lastNameField ? lastNameField.value.trim() : ''
      };
      tagInput.value = buildTags(teamSize);
      saveState({ awaitingResult: true, teamSize: teamSize, identity: identity, klaviyoSynced: false });
      emitEvent('scorecard_submit', {
        result_level: result.level.name,
        priority_gap: result.priorityGap,
        team_route: teamSize === 'just_me' ? 'individual' : 'team'
      });

      if (root.dataset.preview === 'true') {
        event.preventDefault();
        emitEvent('scorecard_complete', {
          result_level: result.level.name,
          priority_gap: result.priorityGap
        });
        renderResult(teamSize);
      }
    });

    root.querySelectorAll('[data-event]').forEach(function (link) {
      link.addEventListener('click', function () {
        emitEvent(link.dataset.event, {
          result_level: result ? result.level.name : '',
          priority_gap: result ? result.priorityGap : ''
        });
      });
    });

    root.querySelector('[data-restart]').addEventListener('click', function () {
      Object.keys(answers).forEach(function (key) { delete answers[key]; });
      questionIndex = 0;
      result = null;
      try { global.sessionStorage.removeItem(storageKey); } catch (error) {}
      showStage('landing');
    });

    const stored = loadState();
    const formSucceeded = formState.dataset.formSuccess === 'true' || new URLSearchParams(global.location.search).get('customer_posted') === 'true';
    const formFailed = formState.dataset.formError === 'true';

    emitEvent('scorecard_view', {});
    if (formSucceeded && stored && stored.result) {
      result = stored.result;
      if (!stored.klaviyoSynced && stored.identity && stored.identity.email) {
        syncKlaviyo(result, stored.teamSize || 'just_me', stored.identity, function () {
          saveState({
            awaitingResult: false,
            teamSize: stored.teamSize || 'just_me',
            identity: stored.identity,
            klaviyoSynced: true
          });
        });
      }
      emitEvent('scorecard_complete', {
        result_level: result.level.name,
        priority_gap: result.priorityGap
      });
      emitEvent('generate_lead', {});
      renderResult(stored.teamSize || 'just_me');
    } else if (formFailed && stored) {
      if (formError) formError.hidden = false;
      showStage('capture');
    } else {
      showStage('landing');
    }
  }

  function boot() {
    global.document.querySelectorAll('[data-aif-scorecard]').forEach(initScorecard);
  }

  const api = {
    DIMENSIONS: DIMENSIONS,
    QUESTIONS: QUESTIONS,
    ANSWERS: ANSWERS,
    LEVELS: LEVELS,
    GAP_COPY: GAP_COPY,
    getLevel: getLevel,
    calculateScore: calculateScore,
    buildKlaviyoProperties: buildKlaviyoProperties,
    initScorecard: initScorecard
  };

  if (typeof module !== 'undefined' && module.exports) module.exports = api;
  global.AIFScorecard = api;

  if (global.document) {
    if (global.document.readyState === 'loading') {
      global.document.addEventListener('DOMContentLoaded', boot);
    } else {
      boot();
    }
    global.document.addEventListener('shopify:section:load', function (event) {
      const root = event.target.querySelector('[data-aif-scorecard]');
      if (root) initScorecard(root);
    });
  }
})(typeof window !== 'undefined' ? window : globalThis);
